import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronRight, CheckCircle, Calendar, Baby, Heart, Users, Camera, Pencil } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { DatePicker } from '../components/ui/date-picker';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SESSION_TYPES = [
  { id: 'recien_nacido', label: 'Recién Nacido', icon: Baby, color: 'text-blue-500 bg-blue-500/10' },
  { id: 'primera_comunion', label: 'Primera Comunión', icon: Heart, color: 'text-purple-500 bg-purple-500/10' },
  { id: 'boda', label: 'Boda', icon: Heart, color: 'text-rose-500 bg-rose-500/10' },
  { id: 'embarazo', label: 'Embarazo', icon: Baby, color: 'text-pink-500 bg-pink-500/10' },
  { id: 'familia', label: 'Familia', icon: Users, color: 'text-green-500 bg-green-500/10' },
];

export const SessionDataModal = ({ isOpen, onClose, onSubmit, existingData = null }) => {
  const [step, setStep] = useState(1); // 1: select type, 2: fill form
  const [sessionType, setSessionType] = useState(null);
  const [formData, setFormData] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const isEditMode = !!existingData;

  // Initialize form when opening with existing data
  useEffect(() => {
    if (isOpen && existingData) {
      const type = SESSION_TYPES.find(t => t.id === existingData.session_type);
      if (type) {
        setSessionType(type);
        setFormData(existingData.data || {});
        setStep(2);
      }
    }
  }, [isOpen, existingData]);

  const resetForm = () => {
    setStep(1);
    setSessionType(null);
    setFormData({});
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSelectType = (type) => {
    setSessionType(type);
    setFormData({});
    setStep(2);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    // Validate required fields
    const requiredFields = getRequiredFields();
    const missingFields = requiredFields.filter(f => f.required !== false && !formData[f.id]?.toString().trim());
    
    if (missingFields.length > 0) {
      toast.error(`Por favor, completa: ${missingFields.map(f => f.label).join(', ')}`);
      return;
    }

    setSubmitting(true);
    try {
      const url = isEditMode 
        ? `${API}/client/session-data/${existingData.session_data_id}`
        : `${API}/client/session-data`;
      
      const method = isEditMode ? 'PUT' : 'POST';
      
      const body = isEditMode 
        ? { data: formData }
        : { session_type: sessionType.id, data: formData };

      const clientToken = sessionStorage.getItem('client_token');
      const response = await fetch(url, {
        method,
        headers: { 
          'Content-Type': 'application/json',
          ...(clientToken ? { 'Authorization': `Bearer ${clientToken}` } : {})
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Error al enviar');
      }

      const result = await response.json();
      
      if (isEditMode) {
        toast.success('¡Datos actualizados correctamente!');
      } else {
        toast.success(result.event_created 
          ? '¡Datos guardados y evento creado en el calendario!' 
          : '¡Datos guardados correctamente!'
        );
      }
      
      onSubmit?.();
      handleClose();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const getRequiredFields = () => {
    if (!sessionType) return [];
    
    const fields = {
      recien_nacido: [
        { id: 'nombre_madre', label: 'Nombre de la madre' },
        { id: 'nombre_padre', label: 'Nombre del padre' },
        { id: 'telefonos', label: 'Teléfonos de contacto' },
        { id: 'direccion', label: 'Dirección' },
        { id: 'localidad', label: 'Localidad' },
        { id: 'nombre_bebe', label: 'Nombre del bebé' },
        { id: 'genero_bebe', label: 'Género' },
        { id: 'fecha_nacimiento', label: 'Fecha de nacimiento' },
        { id: 'fecha_sesion', label: 'Fecha de la sesión' },
      ],
      primera_comunion: [
        { id: 'nombre_madre', label: 'Nombre de la madre' },
        { id: 'nombre_padre', label: 'Nombre del padre' },
        { id: 'telefonos', label: 'Teléfonos de contacto' },
        { id: 'direccion', label: 'Dirección' },
        { id: 'localidad', label: 'Localidad' },
        { id: 'nombre_nino', label: 'Nombre del niño/a' },
        { id: 'genero_nino', label: 'Género' },
        { id: 'fecha_nacimiento', label: 'Fecha de nacimiento' },
        { id: 'localidad_ceremonia', label: 'Localidad de la ceremonia' },
        { id: 'iglesia', label: 'Iglesia' },
        { id: 'fecha_ceremonia', label: 'Fecha de la ceremonia' },
        { id: 'hora_ceremonia', label: 'Hora de la ceremonia' },
      ],
      boda: [
        { id: 'nombre_novia', label: 'Nombre de la novia' },
        { id: 'nombre_novio', label: 'Nombre del novio' },
        { id: 'telefonos', label: 'Teléfonos de contacto' },
        { id: 'direccion', label: 'Dirección' },
        { id: 'localidad', label: 'Localidad' },
        { id: 'preferencia_localizacion_preboda', label: 'Preferencia localización preboda', required: false },
        { id: 'localidad_ceremonia', label: 'Localidad de la ceremonia' },
        { id: 'iglesia', label: 'Iglesia' },
        { id: 'fecha_ceremonia', label: 'Fecha de la ceremonia' },
        { id: 'hora_ceremonia', label: 'Hora de la ceremonia' },
      ],
      embarazo: [
        { id: 'nombre_madre', label: 'Nombre de la madre' },
        { id: 'nombre_padre', label: 'Nombre del padre' },
        { id: 'telefonos', label: 'Teléfonos de contacto' },
        { id: 'direccion', label: 'Dirección' },
        { id: 'localidad', label: 'Localidad' },
        { id: 'fecha_sesion', label: 'Fecha de la sesión' },
        { id: 'nombre_bebe', label: 'Nombre del bebé', required: false },
        { id: 'genero_bebe', label: 'Género del bebé', required: false },
      ],
      familia: [
        { id: 'nombre_madre', label: 'Nombre de la madre' },
        { id: 'nombre_padre', label: 'Nombre del padre' },
        { id: 'telefonos', label: 'Teléfonos de contacto' },
        { id: 'direccion', label: 'Dirección' },
        { id: 'localidad', label: 'Localidad' },
        { id: 'nombres_hijos', label: 'Nombre de los hijos' },
        { id: 'preferencia_localizacion', label: 'Preferencia de localización', required: false },
        { id: 'fecha_sesion', label: 'Fecha de la sesión' },
      ],
    };
    
    return fields[sessionType.id] || [];
  };

  const renderFormFields = () => {
    if (!sessionType) return null;
    
    const fields = getRequiredFields();
    
    // Group fields by section
    const sections = [];
    
    if (sessionType.id === 'recien_nacido') {
      sections.push(
        { title: 'Datos de los padres', fields: fields.slice(0, 5) },
        { title: 'Datos del bebé', fields: fields.slice(5, 8) },
        { title: 'Fecha de la sesión', fields: fields.slice(8) }
      );
    } else if (sessionType.id === 'primera_comunion') {
      sections.push(
        { title: 'Datos de los padres', fields: fields.slice(0, 5) },
        { title: 'Datos del niño/a', fields: fields.slice(5, 8) },
        { title: 'Datos de la ceremonia', fields: fields.slice(8) }
      );
    } else if (sessionType.id === 'boda') {
      sections.push(
        { title: 'Datos de los novios', fields: fields.slice(0, 5) },
        { title: 'Sesión preboda', fields: fields.slice(5, 6) },
        { title: 'Datos de la ceremonia', fields: fields.slice(6) }
      );
    } else if (sessionType.id === 'embarazo') {
      sections.push(
        { title: 'Datos de los padres', fields: fields.slice(0, 6) },
        { title: 'Datos del bebé (opcional)', fields: fields.slice(6) }
      );
    } else if (sessionType.id === 'familia') {
      sections.push(
        { title: 'Datos de la familia', fields: fields }
      );
    }
    
    return sections.map((section, sIdx) => (
      <div key={sIdx} className="space-y-3">
        <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
          {section.title}
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {section.fields.map((field) => (
            <div key={field.id} className={field.id.includes('direccion') || field.id.includes('nombres_hijos') || field.id.includes('preferencia') ? 'sm:col-span-2' : ''}>
              <label className="text-xs text-muted-foreground block mb-1">
                {field.label} {field.required !== false && '*'}
              </label>
              {field.id.includes('genero') ? (
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name={field.id}
                      value="H"
                      checked={formData[field.id] === 'H'}
                      onChange={() => handleInputChange(field.id, 'H')}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">Hombre</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name={field.id}
                      value="M"
                      checked={formData[field.id] === 'M'}
                      onChange={() => handleInputChange(field.id, 'M')}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">Mujer</span>
                  </label>
                </div>
              ) : field.id.includes('fecha') ? (
                <DatePicker
                  value={formData[field.id] || ''}
                  onChange={(date) => handleInputChange(field.id, date)}
                  placeholder="Seleccionar fecha"
                />
              ) : field.id.includes('hora') ? (
                <Input
                  type="time"
                  value={formData[field.id] || ''}
                  onChange={(e) => handleInputChange(field.id, e.target.value)}
                />
              ) : (
                <Input
                  type={field.id.includes('telefono') ? 'tel' : 'text'}
                  value={formData[field.id] || ''}
                  onChange={(e) => handleInputChange(field.id, e.target.value)}
                />
              )}
            </div>
          ))}
        </div>
      </div>
    ));
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={handleClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="bg-background border border-border w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="p-4 border-b border-border flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${sessionType ? sessionType.color : 'bg-muted'}`}>
                {sessionType ? <sessionType.icon className="w-5 h-5" /> : <Camera className="w-5 h-5 text-muted-foreground" />}
              </div>
              <div>
                <h2 className="font-medium">
                  {isEditMode 
                    ? `Editar datos de ${sessionType?.label}`
                    : step === 1 
                      ? 'Informar datos de la sesión' 
                      : `Datos de ${sessionType?.label}`
                  }
                </h2>
                <p className="text-xs text-muted-foreground">
                  {isEditMode
                    ? 'Modifica los datos de tu sesión'
                    : step === 1 
                      ? 'Selecciona el tipo de sesión' 
                      : 'Completa los datos requeridos'
                  }
                </p>
              </div>
            </div>
            <button onClick={handleClose} className="p-2 hover:bg-muted rounded-full">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4">
            {step === 1 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {SESSION_TYPES.map((type) => (
                  <button
                    key={type.id}
                    onClick={() => handleSelectType(type)}
                    className="p-4 border border-border hover:border-foreground/30 hover:bg-muted/50 transition-all text-left flex items-center gap-4 group"
                  >
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${type.color}`}>
                      <type.icon className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                      <span className="font-medium">{type.label}</span>
                    </div>
                    <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-foreground transition-colors" />
                  </button>
                ))}
              </div>
            ) : (
              <div className="space-y-6">
                {renderFormFields()}
              </div>
            )}
          </div>

          {/* Actions */}
          {step === 2 && (
            <div className="p-4 border-t border-border flex gap-3 flex-shrink-0">
              <Button variant="outline" onClick={() => setStep(1)} className="flex-1">
                Atrás
              </Button>
              <Button onClick={handleSubmit} disabled={submitting} className="flex-1 gap-2">
                {submitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-background border-t-transparent rounded-full animate-spin" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Guardar datos
                  </>
                )}
              </Button>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default SessionDataModal;
